import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import LoginContainer from "components/LoginContainer";
import TextInput from "components/TextInput";
import { AUTH_SCREEN_BUTTON_BACKGROUND } from "constants/";
import { Field, Formik } from "formik";
import { ResetPasswordScreenProps } from "navigation/types";
import i18n from "plugins/localization";
import { resetPasswordFetcher } from "queries/user.queries";
import React from "react";
import { View, Pressable } from "react-native";
import { loginInputProps } from "screens/Login/Login";
import toast from "utils/toast";
import * as yup from "yup";

export interface InitialValues {
  password: string;
  confirmPassword: string;
}

const initialValues: InitialValues = {
  password: "",
  confirmPassword: "",
};

export const validationSchema = yup.object().shape({
  password: yup
    .string()
    .min(6, "Minimum length for password is 6 characters")
    .required("Required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Passwords must match"),
});

export default function ResetPassword({
  navigation,
  route,
}: ResetPasswordScreenProps) {
  const { token, encodedEmail } = route.params;
  const { mutateAsync: resetPassword, isLoading: resettingPassword } =
    useMutation(resetPasswordFetcher);
  const toLogin = () => navigation.navigate("Login");

  const handleSubmit = async ({ password, confirmPassword }: InitialValues) => {
    await resetPassword({
      username: window.atob(decodeURIComponent(encodedEmail)),
      password,
      confirmPassword,
      token,
    });
    toast.success(i18n.t("screens.ResetPassword.passwordResetMessage"));
    toLogin();
  };

  return (
    <LoginContainer title={i18n.t("screens.ResetPassword.title")}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isValid, handleSubmit }) => (
          <View>
            <View style={{ marginVertical: 15 }}>
              <Field
                name="password"
                component={TextInput}
                secureTextEntry={true}
                label={
                  <Text style={{ color: "white" }}>
                    {i18n.t("labels.newPassword")}
                  </Text>
                }
                {...loginInputProps}
              ></Field>
            </View>

            <Field
              name="confirmPassword"
              component={TextInput}
              secureTextEntry={true}
              label={
                <Text style={{ color: "white" }}>
                  {i18n.t("labels.confirmPassword")}
                </Text>
              }
              {...loginInputProps}
            ></Field>

            <Button
              disabled={!isValid}
              style={{
                marginVertical: 15,
                backgroundColor: AUTH_SCREEN_BUTTON_BACKGROUND,
                borderWidth: 0,
              }}
              loading={resettingPassword}
              onPress={() => handleSubmit()}
            >
              {i18n.t("screens.ResetPassword.resetButton")}
            </Button>
          </View>
        )}
      </Formik>
      <Pressable onPress={() => navigation.navigate("Login")}>
        <Text style={{ color: "white", textAlign: "center" }}>
          {i18n.t("common.loginButton")}
        </Text>
      </Pressable>
    </LoginContainer>
  );
}
